import { CapacityStatus } from '@core/enums/capacity-status.enum';
import { ProgressBarStatus } from '@core/enums/progress-bar-status.enum';
import { AssignmentPeriod } from '@core/models/assignment-period.model';
import { PlanningBoardSettings } from '@core/models/planning-board-settings';
import { AssignmentPeriodAggregate } from '@core/typings/assignment-period-aggregate';
import { Period } from '@portal-shared/app/entities/period.model';

export class PeriodCapacityHelper {
  public static computeCapacityPercentage(planningSettings: PlanningBoardSettings, value: number, maxValue: number) {
    //no capacity and entered any value means we are above 100%
    if (maxValue <= 0 && value > 0) {
      return planningSettings.overloadedThreshold + 1; //Smallest integer strictly greater than the overloadedThreshold
    }

    const rawPercentage = maxValue ? (100 * value) / maxValue : 0;
    return rawPercentage;
  }

  public static computeStatus(planningSettings: PlanningBoardSettings, percentage: number, resourceCapacity: number) {
    const status = CapacityStatus.Free;

    const { activeThreshold, busyThreshold, loadedThreshold, overloadedThreshold } = planningSettings;

    if (percentage > overloadedThreshold) {
      return CapacityStatus.Overloaded;
    } else if (percentage > loadedThreshold) {
      return CapacityStatus.Full;
    } else if (percentage > busyThreshold) {
      return CapacityStatus.Busy;
    } else if (percentage > activeThreshold) {
      return CapacityStatus.Active;
    } else if (!percentage && !resourceCapacity) {
      return CapacityStatus.Away;
    }

    return status;
  }

  public static toProgressBarStatus(capacityStatus: CapacityStatus) {
    switch (capacityStatus) {
      case CapacityStatus.Free:
      case CapacityStatus.Away:
        return ProgressBarStatus.Neutral;
      case CapacityStatus.Active:
        return ProgressBarStatus.Good;
      case CapacityStatus.Busy:
        return ProgressBarStatus.Warning;
      case CapacityStatus.Full:
        return ProgressBarStatus.Error;
      case CapacityStatus.Overloaded:
        return ProgressBarStatus.Error;
    }
  }

  public static aggregateRowsInPeriodColumn(
    value: (AssignmentPeriod | AssignmentPeriodAggregate | undefined | null)[],
    period: Period
  ) {
    const values = value.filter(Boolean) as (AssignmentPeriod | AssignmentPeriodAggregate)[];

    const initalValue: AssignmentPeriodAggregate = {
      period,
      assignedHours: 0,
      unassignedHours: 0
    };

    if (!values.length) {
      return initalValue;
    }

    const sum = values.reduce((previous: AssignmentPeriodAggregate, current) => {
      const hasRealHours = previous.realHours != undefined || current.realHours != undefined;

      if (current instanceof AssignmentPeriod) {
        if (current.assignment?.resourceId || current.assignment.fictionalResourceId) {
          previous.assignedHours = (previous.assignedHours ?? 0) + (current.plannedHours ?? 0);
        } else if (!(current.assignment?.requestedResourceId && hasRealHours)) {
          previous.unassignedHours = (previous.unassignedHours ?? 0) + (current.plannedHours ?? 0);
        }
      } else {
        previous.assignedHours = (previous.assignedHours ?? 0) + (current.assignedHours ?? 0);
        previous.unassignedHours = (previous.unassignedHours ?? 0) + (current.unassignedHours ?? 0);
      }

      if (hasRealHours) {
        const realHours = (previous.realHours ?? 0) + (current.realHours ?? 0);
        previous.realHours = realHours;
      }
      return previous;
    }, initalValue);

    return sum;
  }

  public static computeCapacity(resourceCapacity: number, timeoff: number) {
    return Math.max(resourceCapacity - timeoff, 0);
  }
}
